import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';

const Scanner = () => {
    const { config } = useConfig();
    const { user, updateUser } = useAuth();
    const [scanResult, setScanResult] = useState(null);
    const [isScanning, setIsScanning] = useState(true);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scanMode, setScanMode] = useState('');
    const [isChangingSala, setIsChangingSala] = useState(false);
    const [salas, setSalas] = useState([]);
    const [selectedSalaId, setSelectedSalaId] = useState('');

    useEffect(() => {
        if (isChangingSala && salas.length === 0) {
            api.get('/salas').then(res => setSalas(res.data)).catch(console.error);
        }
    }, [isChangingSala, salas.length]);

    const handleSaveSala = async () => {
        if (!selectedSalaId) return;
        try {
            const res = await api.put('/users/profile/update', { sala_id: selectedSalaId });
            updateUser(res.data.user);
            setIsChangingSala(false);
        } catch (err) {
            console.error("Error updating sala", err);
        }
    };

    const primaryColor = config?.primaryColor || '#1e40af';

    useEffect(() => {
        let scanner = null;
        
        if (isScanning) {
            scanner = new Html5QrcodeScanner('reader', {
                qrbox: {
                    width: 250,
                    height: 250,
                },
                fps: 5,
                supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
            });

            scanner.render(onScanSuccess, onScanError);
        }

        function onScanSuccess(result) {
            scanner.clear();
            setIsScanning(false);
            
            let parsedId = result;
            try {
                const data = JSON.parse(result);
                if (data.id) {
                    parsedId = data.id;
                }
            } catch (e) {
                // Not JSON, use raw result
            }
            
            processScan(parsedId);
        }

        function onScanError(err) {
            // console.warn(err);
        }

        return () => {
            if (scanner) {
                scanner.clear().catch(e => console.error("Scanner cleanup error", e));
            }
        };
    }, [isScanning]);

    const processScan = async (userId) => {
        setLoading(true);
        setError(null);
        try {
            const payload = { userId };
            if (scanMode) {
                payload.type = scanMode;
            }
            const response = await api.post('/attendance/scan', payload);
            setScanResult(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al procesar el escaneo');
        } finally {
            setLoading(false);
        }
    };

    const handleScannerReset = () => {
        setScanResult(null);
        setError(null);
        setIsScanning(true);
    };

    return (
        <div className="animate-fadeIn min-h-[60vh] flex flex-col items-center">
            <header className="mb-8 w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-black tracking-tight italic uppercase tracking-tighter">Escáner de Asistencia</h1>
                    <p className="text-black/60 font-medium mt-1 uppercase tracking-widest text-[10px]">Registra entrada y salida de asistentes.</p>
                </div>
                {user?.sala_name && (
                    <div className="flex flex-col items-end gap-2">
                        <div style={{ background: 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)' }} className="px-4 py-2.5 rounded-md flex items-center gap-2 shadow-lg">
                            <span className="text-sm">🏢</span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white">Sala: {user.sala_name}</span>
                        </div>
                        <button 
                            onClick={() => setIsChangingSala(true)} 
                            className="text-[9px] font-black text-black/50 hover:text-black uppercase tracking-widest underline decoration-black/30 underline-offset-2"
                        >
                            Cambiar Sala
                        </button>
                    </div>
                )}
            </header>

            <div className="w-full max-w-md bg-white/10 backdrop-blur-2xl rounded-[50px] border border-white/20 shadow-2xl overflow-hidden p-8 sm:p-10">
                {isScanning && (
                    <div className="flex justify-center gap-2 mb-6">
                        <button 
                            onClick={() => setScanMode('')}
                            className={`px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === '' ? 'bg-black text-white shadow-md' : 'bg-black/5 text-black/60 hover:bg-black/10 hover:text-black'}`}
                        >
                            Auto
                        </button>
                        <button 
                            onClick={() => setScanMode('entry')}
                            className={`px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === 'entry' ? 'bg-emerald-500 text-white shadow-md' : 'bg-black/5 text-black/60 hover:bg-black/10 hover:text-black'}`}
                        >
                            Ingreso
                        </button>
                        <button 
                            onClick={() => setScanMode('break')}
                            className={`px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === 'break' ? 'bg-blue-500 text-white shadow-md' : 'bg-black/5 text-black/60 hover:bg-black/10 hover:text-black'}`}
                        >
                            Break
                        </button>
                        <button 
                            onClick={() => setScanMode('exit')}
                            className={`px-4 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === 'exit' ? 'bg-orange-500 text-white shadow-md' : 'bg-black/5 text-black/60 hover:bg-black/10 hover:text-black'}`}
                        >
                            Salida
                        </button>
                    </div>
                )}
                {isScanning ? (
                    <div className="relative">
                        <div id="reader" className="overflow-hidden rounded-3xl border-none shadow-2xl bg-black/20"></div>
                        <div className="mt-10 text-center">
                            <div style={{ background: 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)' }} className="inline-block px-6 py-3 rounded-md text-[10px] font-black uppercase tracking-[0.3em] text-white shadow-lg animate-pulse">
                                Buscando código QR...
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-8 flex flex-col items-center text-center">
                        {loading ? (
                            <div className="flex flex-col items-center py-10">
                                <div className="w-12 h-12 border-4 border-white/10 border-t-white rounded-full animate-spin"></div>
                                <p className="mt-6 font-black text-[10px] uppercase tracking-[0.4em] text-white/40">Procesando...</p>
                            </div>
                        ) : scanResult ? (
                            <div className="space-y-8 animate-scaleUp">
                                <div 
                                    className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 ${
                                        scanResult.type === 'entry' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 
                                        scanResult.type === 'break' ? 'bg-blue-500/20 border-blue-500 text-blue-400' :
                                        'bg-orange-500/20 border-orange-500 text-orange-400'
                                    }`}
                                >
                                    {scanResult.type === 'entry' ? '✓' : scanResult.type === 'break' ? '☕' : '👋'}
                                </div>
                                <div>
                                    <h2 
                                        style={{ 
                                            background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            color: 'transparent'
                                        }}
                                        className="text-3xl font-black leading-tight uppercase italic tracking-tighter"
                                    >
                                        {scanResult.user.name} <br/> {scanResult.user.last_name}
                                    </h2>
                                    <p className={`font-black uppercase tracking-[0.3em] text-[11px] mt-4 py-2 px-4 rounded-full inline-block ${
                                        scanResult.type === 'entry' ? 'bg-emerald-500 text-white' : 
                                        scanResult.type === 'break' ? 'bg-blue-500 text-white' :
                                        'bg-orange-500 text-white'
                                    }`}>
                                        {scanResult.type === 'entry' ? 'Entrada Registrada' : 
                                         scanResult.type === 'break' ? 'Break Registrado' : 
                                         'Salida Registrada'}
                                    </p>
                                    
                                    {scanResult.sessionTitle && (
                                        <p style={{ background: 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)' }} className="text-white text-[11px] mt-4 font-bold uppercase tracking-wider py-2 px-4 rounded-md shadow-lg">
                                            Charla: {scanResult.sessionTitle}
                                        </p>
                                    )}
                                    
                                    {scanResult.salaName && (
                                        <p style={{ background: 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)' }} className="text-white text-[11px] mt-2 font-bold uppercase tracking-wider py-2 px-4 rounded-md shadow-lg">
                                            Sala: {scanResult.salaName}
                                        </p>
                                    )}
                                    
                                    {scanResult.timestamp && (
                                        <p className="text-black/60 text-[10px] mt-2 font-medium uppercase tracking-widest">
                                            {new Date(scanResult.timestamp).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                                        </p>
                                    )}
                                    
                                    <p className="text-black/40 text-[10px] mt-6 font-black uppercase tracking-widest">Identificación: {scanResult.user.identification}</p>
                                </div>
                                <button 
                                    onClick={handleScannerReset}
                                    style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                                    className="w-full py-5 text-white rounded-md font-black uppercase tracking-[0.2em] text-[11px] hover:scale-105 active:scale-95 transition-all shadow-2xl"
                                >
                                    Escanear Siguiente
                                </button>
                            </div>
                        ) : error ? (
                            <div className="space-y-8 animate-shake">
                                <div className="w-24 h-24 mx-auto bg-red-500/20 border-4 border-red-500 text-red-400 rounded-full flex items-center justify-center text-4xl shadow-2xl">
                                    ✕
                                </div>
                                <div>
                                    <h2 className="text-2xl font-black text-black uppercase italic">Error en Escaneo</h2>
                                    <p className="text-red-600 font-bold mt-4 uppercase tracking-tight text-xs bg-red-500/10 p-4 rounded-2xl border border-red-500/20 shadow-sm">{error}</p>
                                </div>
                                <button 
                                    onClick={handleScannerReset}
                                    style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                                    className="w-full py-5 text-white rounded-md font-black uppercase tracking-[0.2em] text-[11px] hover:scale-105 active:scale-95 transition-all shadow-2xl"
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
            
            <div className="mt-12 flex flex-col items-center gap-6">
                <p className="text-[9px] font-black text-black/20 uppercase tracking-[0.5em] italic">Staff Only Control</p>
                <div className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-black/10"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-black/10"></div>
                </div>
            </div>

            {isChangingSala && (
                <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/95 p-4 animate-fadeIn">
                    <div className="bg-slate-900 border border-white/10 rounded-[40px] p-8 max-w-md w-full space-y-6 shadow-2xl relative text-center">
                        <button onClick={() => setIsChangingSala(false)} className="absolute top-6 right-6 text-white/50 hover:text-white">✕</button>
                        <div className="flex justify-center mb-2">
                            <span className="text-5xl">🏢</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">Cambiar Sala</h3>
                            <p className="text-white/60 text-xs leading-relaxed">
                                Selecciona la nueva sala donde estarás registrando la asistencia.
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div className="relative">
                                <select 
                                    className="w-full bg-white/5 border border-white/10 text-white p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all appearance-none"
                                    value={selectedSalaId}
                                    onChange={(e) => setSelectedSalaId(e.target.value)}
                                >
                                    <option value="" disabled className="bg-slate-900 text-white">Selecciona una sala...</option>
                                    {salas.map(s => (
                                        <option key={s.id} value={s.id} className="bg-slate-900 text-white">
                                            {s.name} {s.ubication ? `(${s.ubication})` : ''}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none">▼</div>
                            </div>
                        </div>
                        <button
                            onClick={handleSaveSala}
                            disabled={!selectedSalaId}
                            style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                            className="w-full text-white py-4 rounded-md font-black uppercase tracking-widest text-[11px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                        >
                            GUARDAR SALA
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Scanner;
